import {
  FormControlLabel,
  type FormControlLabelProps,
  Switch,
  type TypographyProps,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { globalInteractions } from '@/common/atoms/global';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';
import { Scopes } from '$authentication/useHasPermission/constants';
import { useHasPermission } from '$authentication/useHasPermission/useHasPermission';

interface Props extends Pick<FormControlLabelProps, 'labelPlacement' | 'sx'> {
  label?: string;
  typographyProps?: TypographyProps;
  eventContainer?: string;
}

export function GlobalToggle({
  label = 'Show global',
  labelPlacement = 'start',
  typographyProps,
  sx,
  eventContainer = 'transactions_list_subheader',
}: Props) {
  const { hasPermission: hasInteractionsReadPermission } = useHasPermission({
    roles: ['ADMIN'],
    scopes: [Scopes.INTERACTIONS_READ],
  });
  const [showGlobalInteractions, setShowGlobalInteractions] =
    useAtom(globalInteractions);
  const trackEvent = useTrackInteractionsAnalytics();

  const handleGlobalSwitchOnChange = useCallback(() => {
    setShowGlobalInteractions((prevState) => {
      trackEvent(TrackInteractionsEvent.ClickGlobalToggle, {
        eventContainer,
        task: prevState ? 'deselect' : 'select',
      });
      return !prevState;
    });
  }, [setShowGlobalInteractions, trackEvent, eventContainer]);

  if (!hasInteractionsReadPermission) {
    return null;
  }

  return (
    <FormControlLabel
      label={label}
      labelPlacement={labelPlacement}
      slotProps={{
        typography: typographyProps,
      }}
      control={
        <Switch
          color="default"
          size="large"
          checked={showGlobalInteractions}
          onChange={handleGlobalSwitchOnChange}
        />
      }
      sx={{
        // Remove left/top margin to keep alignment
        m: -0.75,
        ...sx,
      }}
    />
  );
}
